import { requireAuth } from '@/features/auth/guards/require-auth'
import { GlobalNomenclaturePage } from '@/features/bom/pages/global-nomenclature-page'
import { projectMaterialsQuery } from '@/features/bom/queries/bom-query'
import { globalNomenclatureTreeQuery } from '@/features/bom/queries/global-nomenclature-tree-query'
import { globalSheetSearchSchema, nodeKindSchema } from '@/features/bom/types'
import { messages } from '@/lib/i18n/messages'
import { createFileRoute } from '@tanstack/react-router'
import type * as z from 'zod'

/**
 * Top-level cross-project Nomenclature page (1С-style). Listed under the
 * "Global" sidebar group; URL: `/nomenclature/`.
 *
 * Nested under the shared `_app` layout (which renders the Shell once), so
 * this route only renders its page — no own `<Shell>`. It lives outside the
 * `projects/$projectId/_layout` namespace but shares the global sidebar/topbar.
 */
export const Route = createFileRoute('/_app/nomenclature')({
  validateSearch: globalSheetSearchSchema,
  beforeLoad: requireAuth,
  // Hoist `kind` validation so deeplinks like `?kind=part` round-trip without
  // crashing the loader. `z.coerce.number()` lets `?project=1` parse straight
  // from the URL string into a number.
  loaderDeps: ({ search }) => ({
    project: search.project,
    kind: search.kind ? nodeKindSchema.parse(search.kind) : undefined,
  }),
  loader: ({ context: { queryClient } }) =>
    Promise.all([
      queryClient.ensureQueryData(globalNomenclatureTreeQuery()),
      queryClient.ensureQueryData(projectMaterialsQuery(0)),
    ]),
  staticData: { crumb: messages.nav.globalNomenclature },
  component: GlobalNomenclatureRoute,
})

function GlobalNomenclatureRoute() {
  return <GlobalNomenclaturePage />
}

export type GlobalSearch = z.infer<typeof globalSheetSearchSchema>
